import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Image } from 'lucide-react';

// Backend URL for images
const BACKEND_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:5001';

// Helper to construct full image URL
const getImageUrl = (url: string | undefined): string => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('//')) return `https:${url}`;
  return `${BACKEND_URL}${url.startsWith('/') ? url : `/${url}`}`;
};

interface Section {
  id: string;
  type: 'banner' | 'image-text' | 'features' | 'slider' | 'highlights' | 'infographic' |
    'hero-banner' | 'gradient-banner' | 'video-banner' | 'image-carousel' | 'image-gallery' |
    'image-text-left' | 'image-text-right' | 'features-grid' | 'feature-cards' |
    'product-showcase' | 'stats-counter' | 'trust-badges' | 'faq-section' |
    'before-after' | 'comparison-table' | 'cinematic-text' | 'sticky-cta' |
    'frequently-bought' | 'hotspots' | 'rich-heading' | 'video-gallery';
  order: number;
  content: any;
  isEnabled?: boolean;
  position?: string;
}

interface ProductDescriptionRendererProps {
  sections: Section[];
  position?: string;
}

const ProductDescriptionRenderer: React.FC<ProductDescriptionRendererProps> = ({ sections, position }) => {
  if (!sections || sections.length === 0) return null;

  // Filter sections by position if specified
  const filteredSections = position
    ? sections.filter(s => {
        if (s.isEnabled === false) return false;
        const sectionPos = s.position || 'default';
        // For description tab positions, group them together
        if (position === 'description') {
          return ['default', 'above-tabs', 'after-tabs'].includes(sectionPos);
        }
        return sectionPos === position;
      })
    : sections.filter(s => s.isEnabled !== false);

  if (filteredSections.length === 0) return null;

  return (
    <div className="w-full space-y-3">
      {filteredSections.map((section) => (
        <div
          key={section.id}
          className="bg-white border border-gray-200 rounded-lg overflow-hidden"
        >
          <SectionRenderer section={section} />
        </div>
      ))}
    </div>
  );
};

const SectionRenderer: React.FC<{ section: Section }> = ({ section }) => {
  // Skip disabled sections
  if (section.isEnabled === false) return null;

  switch (section.type) {
    // Legacy types
    case 'banner':
      return <BannerSection content={section.content} />;
    case 'image-text':
      return <ImageTextSection content={section.content} />;
    case 'features':
      return <FeaturesSection content={section.content} />;
    case 'slider':
      return <SliderSection content={section.content} />;
    case 'highlights':
      return <HighlightsSection content={section.content} />;
    case 'infographic':
      return <InfographicSection content={section.content} />;

    // New Premium types
    case 'hero-banner':
    case 'gradient-banner':
      return <HeroBannerSection content={section.content} />;
    case 'image-carousel':
    case 'image-gallery':
      return <ImageCarouselSection content={section.content} />;
    case 'video-banner':
    case 'video-gallery':
      return <VideoBannerSection content={section.content} />;
    case 'image-text-left':
      return <ImageTextLeftSection content={section.content} />;
    case 'image-text-right':
      return <ImageTextRightSection content={section.content} />;
    case 'features-grid':
    case 'feature-cards':
      return <FeaturesGridSection content={section.content} />;
    case 'product-showcase':
      return <ProductShowcaseSection content={section.content} />;
    case 'stats-counter':
      return <StatsCounterSection content={section.content} />;
    case 'trust-badges':
      return <TrustBadgesSection content={section.content} />;
    case 'faq-section':
      return <FAQSection content={section.content} />;
    case 'before-after':
      return <BeforeAfterSection content={section.content} />;
    case 'comparison-table':
      return <ComparisonTableSection content={section.content} />;
    case 'cinematic-text':
      return <CinematicTextSection content={section.content} />;
    case 'sticky-cta':
      return <StickyCTASection content={section.content} />;
    case 'frequently-bought':
      return <FrequentlyBoughtSection content={section.content} />;
    case 'hotspots':
      return <HotspotsSection content={section.content} />;
    case 'rich-heading':
      return <RichHeadingSection content={section.content} />;

    default:
      console.warn(`Unknown section type: ${section.type}`);
      return null;
  }
};

// Banner Section - Professional
const BannerSection: React.FC<any> = ({ content }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const { images, overlay, overlayText, overlayPosition } = content;

  useEffect(() => {
    if (!images || images.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % images.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [images]);

  if (!images?.length) return null;

  const fullImages = images.map((img: string) => getImageUrl(img));

  return (
    <div className="relative w-full h-[200px] sm:h-[250px] md:h-[300px] overflow-hidden bg-gray-100">
      {fullImages.map((img: string, idx: number) => (
        <div
          key={idx}
          className={`absolute inset-0 transition-opacity duration-500 ${idx === currentSlide ? 'opacity-100' : 'opacity-0'}`}
        >
          <img src={img} alt={`Banner ${idx + 1}`} className="w-full h-full object-cover" />
        </div>
      ))}

      {overlay && <div className="absolute inset-0 bg-black/40" />}

      {overlayText && (
        <div className={`absolute inset-0 flex items-center justify-center p-4 ${
          overlayPosition === 'top' ? 'items-start pt-8' :
          overlayPosition === 'bottom' ? 'items-end pb-8' : ''
        }`}>
          <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-white text-center">
            {overlayText}
          </h2>
        </div>
      )}

      {images.length > 1 && (
        <>
          <button
            onClick={() => setCurrentSlide((prev) => (prev - 1 + images.length) % images.length)}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center hover:bg-white shadow-md"
            aria-label="Previous"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            onClick={() => setCurrentSlide((prev) => (prev + 1) % images.length)}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center hover:bg-white shadow-md"
            aria-label="Next"
          >
            <ChevronRight size={16} />
          </button>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {images.map((_: any, idx: number) => (
              <button
                key={idx}
                onClick={() => setCurrentSlide(idx)}
                className={`w-2 h-2 rounded-full transition-all ${idx === currentSlide ? 'bg-white w-4' : 'bg-white/50'}`}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Image + Text Section - Professional
const ImageTextSection: React.FC<any> = ({ content }) => {
  const { layout, image, heading, description, buttonText, buttonLink, bgColor } = content;
  const imageFirst = layout === 'left-image';

  const fullImage = getImageUrl(image) || null;

  return (
    <section className="p-6 sm:p-8" style={{ backgroundColor: bgColor || '#ffffff' }}>
      <div className="max-w-4xl mx-auto">
        <div className={`grid md:grid-cols-2 gap-6 items-center ${imageFirst ? '' : 'md:grid-flow-dense'}`}>
          <div className={`${imageFirst ? '' : 'md:col-start-2'}`}>
            {fullImage ? (
              <img
                src={fullImage}
                alt={heading || 'Product image'}
                className="w-full rounded-lg"
              />
            ) : (
              <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
                <Image size={32} className="text-gray-400" />
              </div>
            )}
          </div>
          <div className={`${imageFirst ? '' : 'md:col-start-1 md:row-start-1'}`}>
            {heading && (
              <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900 mb-3">
                {heading}
              </h2>
            )}
            {description && (
              <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                {description}
              </p>
            )}
            {buttonText && (
              <a
                href={buttonLink || '#'}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm rounded-lg font-medium hover:bg-gray-800 transition-colors"
              >
                {buttonText}
                <ChevronRight size={16} />
              </a>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Features Section - Professional
const FeaturesSection: React.FC<any> = ({ content }) => {
  const { title, items, columns } = content;
  const colClass = columns === 2 ? 'grid-cols-2' : columns === 4 ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-2 md:grid-cols-3';

  return (
    <section className="p-6 sm:p-8 bg-white">
      <div className="max-w-4xl mx-auto">
        {title && (
          <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900 mb-6">
            {title}
          </h2>
        )}
        <div className={`grid ${colClass} gap-4`}>
          {items?.map((item: any, idx: number) => (
            <div key={idx} className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-xl mb-2">{item.icon}</div>
              <h3 className="font-medium text-gray-900 text-sm mb-1">{item.title}</h3>
              <p className="text-gray-600 text-xs">{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Slider Section - Professional
const SliderSection: React.FC<any> = ({ content }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const { slides, autoPlay, showArrows, showDots } = content;

  useEffect(() => {
    if (!autoPlay || !slides || slides.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides, autoPlay]);

  if (!slides?.length) return null;

  return (
    <section className="p-6 sm:p-8 bg-gray-50">
      <div className="max-w-4xl mx-auto">
        <div className="relative rounded-lg overflow-hidden bg-white border border-gray-200">
          <div className="aspect-video relative">
            {slides.map((slide: any, idx: number) => {
              const fullSlideImage = getImageUrl(slide.image) || null;
              return (
                <div
                  key={idx}
                  className={`absolute inset-0 transition-opacity duration-500 ${idx === currentSlide ? 'opacity-100' : 'opacity-0'}`}
                >
                  {fullSlideImage ? (
                    <img
                      src={fullSlideImage}
                      alt={slide.caption || `Slide ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                      <Image size={32} className="text-gray-400" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {showArrows && slides.length > 1 && (
            <>
              <button
                onClick={() => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length)}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center hover:bg-white shadow-md"
                aria-label="Previous"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={() => setCurrentSlide((prev) => (prev + 1) % slides.length)}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center hover:bg-white shadow-md"
                aria-label="Next"
              >
                <ChevronRight size={16} />
              </button>
            </>
          )}

          {showDots && slides.length > 1 && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
              {slides.map((_: any, idx: number) => (
                <button
                  key={idx}
                  onClick={() => setCurrentSlide(idx)}
                  className={`w-2 h-2 rounded-full transition-all ${idx === currentSlide ? 'bg-gray-900 w-4' : 'bg-gray-400'}`}
                  aria-label={`Go to slide ${idx + 1}`}
                />
              ))}
            </div>
          )}
        </div>

        {slides[currentSlide]?.caption && (
          <p className="text-center mt-3 text-sm text-gray-600">
            {slides[currentSlide].caption}
          </p>
        )}
      </div>
    </section>
  );
};

// Highlights Section - Professional
const HighlightsSection: React.FC<any> = ({ content }) => {
  const { title, items, bgColor, accentColor } = content;

  return (
    <section className="p-6 sm:p-8" style={{ backgroundColor: bgColor || '#f9fafb' }}>
      <div className="max-w-4xl mx-auto">
        {title && (
          <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900 mb-6">
            {title}
          </h2>
        )}
        <div className="flex flex-wrap gap-2">
          {items?.map((item: string, idx: number) => (
            <div
              key={idx}
              className="px-3 py-1.5 rounded-full text-xs font-medium text-white"
              style={{ backgroundColor: accentColor || '#1f2937' }}
            >
              {item}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Infographic Section - Professional
const InfographicSection: React.FC<any> = ({ content }) => {
  const { title, items, layout = 'horizontal' } = content;

  if (!items?.length) return null;

  const isHorizontal = layout === 'horizontal';
  const isGrid = layout === 'grid';

  return (
    <section className="p-6 sm:p-8">
      <div className="max-w-4xl mx-auto">
        {title && (
          <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900 mb-6">
            {title}
          </h2>
        )}

        <div className={
          isHorizontal
            ? 'flex flex-wrap gap-4'
            : isGrid
            ? 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4'
            : 'flex flex-col gap-4 max-w-md mx-auto'
        }>
          {items.map((item: any, idx: number) => (
            <div
              key={idx}
              className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg"
            >
              {item.icon && (
                <div className="flex-shrink-0 w-10 h-10 bg-white border border-gray-200 rounded-lg flex items-center justify-center">
                  <span className="text-lg">{item.icon}</span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                {item.label && (
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">
                    {item.label}
                  </p>
                )}
                {item.value && (
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {item.value}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ===== NEW PREMIUM SECTION COMPONENTS =====

// Hero Banner Section - Professional
const HeroBannerSection: React.FC<any> = ({ content }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const { images, title, subtitle, overlayColor, textColor = '#ffffff' } = content;

  useEffect(() => {
    if (!images || images.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % images.length);
    }, content.interval || 5000);
    return () => clearInterval(timer);
  }, [images, content.interval]);

  if (!images?.length) return null;

  const fullImages = images.map((img: string) => getImageUrl(img));

  return (
    <section className="relative w-full h-[250px] sm:h-[350px] md:h-[400px] overflow-hidden bg-gray-100">
      {fullImages.map((img: string, idx: number) => (
        <div
          key={idx}
          className={`absolute inset-0 transition-opacity duration-500 ${idx === currentSlide ? 'opacity-100' : 'opacity-0'}`}
        >
          <img src={img} alt={`Banner ${idx + 1}`} className="w-full h-full object-cover" />
          {(title || subtitle) && idx === currentSlide && (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center p-4 sm:p-8"
              style={{ backgroundColor: overlayColor || 'rgba(0,0,0,0.5)' }}
            >
              {title && (
                <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold text-center mb-2" style={{ color: textColor }}>
                  {title}
                </h2>
              )}
              {subtitle && (
                <p className="text-sm sm:text-base text-center max-w-2xl" style={{ color: textColor }}>
                  {subtitle}
                </p>
              )}
            </div>
          )}
        </div>
      ))}

      {fullImages.length > 1 && (
        <>
          <button
            onClick={() => setCurrentSlide((prev) => (prev - 1 + fullImages.length) % fullImages.length)}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center hover:bg-white shadow-md"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            onClick={() => setCurrentSlide((prev) => (prev + 1) % fullImages.length)}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center hover:bg-white shadow-md"
          >
            <ChevronRight size={16} />
          </button>
        </>
      )}
    </section>
  );
};

// Image Carousel Section - Professional
const ImageCarouselSection: React.FC<any> = ({ content }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const { images, autoPlay = true, interval = 4000 } = content;

  useEffect(() => {
    if (!autoPlay || !images || images.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % images.length);
    }, interval);
    return () => clearInterval(timer);
  }, [images, autoPlay, interval]);

  if (!images?.length) return null;

  const fullImages = images.map((img: string) => getImageUrl(img));

  return (
    <section className="p-0">
      <div className="relative w-full h-[200px] sm:h-[300px] md:h-[350px] overflow-hidden bg-gray-100">
        {fullImages.map((img: string, idx: number) => (
          <div
            key={idx}
            className={`absolute inset-0 transition-opacity duration-500 ${idx === currentSlide ? 'opacity-100' : 'opacity-0'}`}
          >
            <img src={img} alt={`Slide ${idx + 1}`} className="w-full h-full object-cover" />
          </div>
        ))}

        {fullImages.length > 1 && (
          <>
            <button
              onClick={() => setCurrentSlide((prev) => (prev - 1 + fullImages.length) % fullImages.length)}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center hover:bg-white shadow-md"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={() => setCurrentSlide((prev) => (prev + 1) % fullImages.length)}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center hover:bg-white shadow-md"
            >
              <ChevronRight size={16} />
            </button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
              {fullImages.map((_: any, idx: number) => (
                <button
                  key={idx}
                  onClick={() => setCurrentSlide(idx)}
                  className={`w-2 h-2 rounded-full transition-all ${idx === currentSlide ? 'bg-gray-900 w-4' : 'bg-gray-400'}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

// Video Banner Section
const VideoBannerSection: React.FC<any> = ({ content }) => {
  const { videoUrl, posterImage, title, autoplay = true, muted = true } = content;

  if (!videoUrl) return null;

  const fullPoster = getImageUrl(posterImage) || undefined;

  return (
    <section className="w-full relative">
      <div className="relative w-full h-[250px] sm:h-[350px] md:h-[450px] overflow-hidden">
        <video
          src={videoUrl}
          poster={fullPoster}
          autoPlay={autoplay}
          muted={muted}
          loop
          playsInline
          className="w-full h-full object-cover"
        />
        {title && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white text-center px-4">{title}</h2>
          </div>
        )}
      </div>
    </section>
  );
};

// Image Text Left Section - Professional
const ImageTextLeftSection: React.FC<any> = ({ content }) => {
  const { image, title, description, bgColor = '#f8fafc' } = content;
  if (!image) return null;

  const fullImage = getImageUrl(image);

  return (
    <section className="p-6 sm:p-8" style={{ backgroundColor: bgColor }}>
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-col md:flex-row items-center gap-6">
          <div className="w-full md:w-1/2">
            <img src={fullImage} alt={title || 'Product'} className="w-full h-auto rounded-lg" />
          </div>
          <div className="w-full md:w-1/2 text-center md:text-left">
            {title && <h2 className="text-lg sm:text-xl md:text-2xl font-semibold mb-3">{title}</h2>}
            {description && <p className="text-sm text-gray-600 leading-relaxed">{description}</p>}
          </div>
        </div>
      </div>
    </section>
  );
};

// Image Text Right Section - Professional
const ImageTextRightSection: React.FC<any> = ({ content }) => {
  const { image, title, description, bgColor = '#f8fafc' } = content;
  if (!image) return null;

  const fullImage = getImageUrl(image);

  return (
    <section className="p-6 sm:p-8" style={{ backgroundColor: bgColor }}>
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-col md:flex-row-reverse items-center gap-6">
          <div className="w-full md:w-1/2">
            <img src={fullImage} alt={title || 'Product'} className="w-full h-auto rounded-lg" />
          </div>
          <div className="w-full md:w-1/2 text-center md:text-left">
            {title && <h2 className="text-lg sm:text-xl md:text-2xl font-semibold mb-3">{title}</h2>}
            {description && <p className="text-sm text-gray-600 leading-relaxed">{description}</p>}
          </div>
        </div>
      </div>
    </section>
  );
};

// Features Grid Section - Professional
const FeaturesGridSection: React.FC<any> = ({ content }) => {
  const { title, features = [], columns = 3, bgColor = '#ffffff' } = content;

  return (
    <section className="p-6 sm:p-8" style={{ backgroundColor: bgColor }}>
      <div className="max-w-4xl mx-auto">
        {title && <h2 className="text-lg sm:text-xl md:text-2xl font-semibold mb-6">{title}</h2>}
        <div className={`grid gap-4 ${columns === 2 ? 'grid-cols-2' : columns === 4 ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3'}`}>
          {features.map((feature: any, idx: number) => (
            <div key={idx} className="p-4 bg-gray-50 rounded-lg">
              {feature.icon && <div className="text-2xl mb-2">{feature.icon}</div>}
              <h3 className="font-medium text-sm mb-1">{feature.title}</h3>
              <p className="text-xs text-gray-600">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Product Showcase Section - Professional
const ProductShowcaseSection: React.FC<any> = ({ content }) => {
  const { images = [], title, description, showThumbnails = true, enableZoom = true, enableFullscreen = true, autoPlay = false, interval = 3000 } = content;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (autoPlay && images.length > 1) {
      const timer = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % images.length);
      }, interval);
      return () => clearInterval(timer);
    }
  }, [autoPlay, interval, images.length]);

  if (!images.length) return null;

  const currentImage = images[currentIndex];
  const fullImage = getImageUrl(currentImage);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const handleThumbnailClick = (index: number) => {
    setCurrentIndex(index);
  };

  const handleZoomToggle = () => {
    if (enableZoom) {
      setIsZoomed(!isZoomed);
    }
  };

  const handleFullscreenToggle = () => {
    if (enableFullscreen) {
      setIsFullscreen(!isFullscreen);
    }
  };

  return (
    <section className={`p-6 sm:p-8 bg-gray-50 ${isFullscreen ? 'fixed inset-0 z-50 bg-black' : ''}`}>
      <div className="max-w-5xl mx-auto">
        {title && <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-center mb-2">{title}</h2>}
        {description && <p className="text-sm text-gray-600 text-center mb-6">{description}</p>}
        
        <div className="relative">
          {/* Main Image */}
          <div
            className={`relative overflow-hidden rounded-lg bg-white cursor-pointer transition-all duration-300 ${
              isZoomed ? 'scale-150' : 'scale-100'
            }`}
            onClick={handleZoomToggle}
          >
            <img
              src={fullImage}
              alt={`Product angle ${currentIndex + 1}`}
              className="w-full h-auto object-contain"
              style={{ maxHeight: isFullscreen ? '85vh' : '600px' }}
            />
            
            {/* Navigation Arrows */}
            {images.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); handlePrevious(); }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-white/90 hover:bg-white rounded-full shadow-lg transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleNext(); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white/90 hover:bg-white rounded-full shadow-lg transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </>
            )}

            {/* Fullscreen Button */}
            {enableFullscreen && (
              <button
                onClick={(e) => { e.stopPropagation(); handleFullscreenToggle(); }}
                className="absolute top-4 right-4 p-2 bg-white/90 hover:bg-white rounded-full shadow-lg transition-colors"
              >
                {isFullscreen ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                )}
              </button>
            )}

            {/* Zoom Indicator */}
            {enableZoom && (
              <div className="absolute bottom-4 left-4 px-3 py-1 bg-black/50 text-white text-xs rounded-full">
                {isZoomed ? 'Click to zoom out' : 'Click to zoom in'}
              </div>
            )}

            {/* Image Counter */}
            {images.length > 1 && (
              <div className="absolute bottom-4 right-4 px-3 py-1 bg-black/50 text-white text-xs rounded-full">
                {currentIndex + 1} / {images.length}
              </div>
            )}
          </div>

          {/* Thumbnails */}
          {showThumbnails && images.length > 1 && (
            <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
              {images.map((img: string, idx: number) => (
                <button
                  key={idx}
                  onClick={() => handleThumbnailClick(idx)}
                  className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                    idx === currentIndex ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <img
                    src={getImageUrl(img)}
                    alt={`Angle ${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// Stats Counter Section - Professional
const StatsCounterSection: React.FC<any> = ({ content }) => {
  const { title, stats = [], bgColor = '#1e293b', textColor = '#ffffff' } = content;

  return (
    <section className="p-6 sm:p-8" style={{ backgroundColor: bgColor, color: textColor }}>
      <div className="max-w-4xl mx-auto">
        {title && <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-center mb-6">{title}</h2>}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {stats.map((stat: any, idx: number) => (
            <div key={idx} className="text-center">
              <div className="text-xl sm:text-2xl md:text-3xl font-bold mb-1">{stat.value}</div>
              <div className="text-xs opacity-80">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Trust Badges Section - Professional
const TrustBadgesSection: React.FC<any> = ({ content }) => {
  const { badges = [], style = 'horizontal' } = content;

  if (!badges?.length) return null;

  const isHorizontal = style === 'horizontal';
  const isGrid = style === 'grid';

  return (
    <section className="p-6 sm:p-8">
      <div className="max-w-4xl mx-auto">
        <div className={
          isHorizontal
            ? 'flex flex-wrap justify-center items-center gap-6'
            : isGrid
            ? 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4'
            : 'flex flex-col gap-3 max-w-md mx-auto'
        }>
          {badges.map((badge: any, idx: number) => (
            <div
              key={idx}
              className="flex items-center gap-2"
            >
              {badge.icon && (
                <span className="text-base">{badge.icon}</span>
              )}
              <span className="text-sm text-gray-700">{badge.text}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// FAQ Section - Professional
const FAQSection: React.FC<any> = ({ content }) => {
  const { title = 'Frequently Asked Questions', faqs = [] } = content;
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  if (!faqs || faqs.length === 0) return null;

  return (
    <section className="p-6 sm:p-8">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-lg sm:text-xl md:text-2xl font-semibold mb-6">{title}</h2>
        <div className="space-y-2">
          {faqs.map((faq: any, idx: number) => (
            <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                className="w-full px-4 py-3 text-left flex justify-between items-center bg-white hover:bg-gray-50 transition-colors"
              >
                <span className="font-medium text-sm">{faq.question}</span>
                <ChevronRight className={`transform transition-transform ${openIndex === idx ? 'rotate-90' : ''}`} size={16} />
              </button>
              {openIndex === idx && (
                <div className="px-4 py-3 bg-gray-50 text-sm text-gray-600 border-t border-gray-200">
                  {faq.answer}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Before After Section - Professional
const BeforeAfterSection: React.FC<any> = ({ content }) => {
  const { beforeImage, afterImage, beforeLabel = 'Before', afterLabel = 'After' } = content;
  if (!beforeImage || !afterImage) return null;

  const fullBefore = getImageUrl(beforeImage);
  const fullAfter = getImageUrl(afterImage);

  return (
    <section className="p-6 sm:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center">
            <img src={fullBefore} alt="Before" className="w-full h-auto rounded-lg" />
            <span className="inline-block mt-2 px-3 py-1 bg-gray-200 rounded-full text-xs">{beforeLabel}</span>
          </div>
          <div className="text-center">
            <img src={fullAfter} alt="After" className="w-full h-auto rounded-lg" />
            <span className="inline-block mt-2 px-3 py-1 bg-gray-900 text-white rounded-full text-xs">{afterLabel}</span>
          </div>
        </div>
      </div>
    </section>
  );
};

// Comparison Table Section - Professional
const ComparisonTableSection: React.FC<any> = ({ content }) => {
  const { title, features = [], products = [] } = content;

  if (!features.length || !products.length) {
    return null;
  }

  return (
    <section className="p-6 sm:p-8">
      <div className="max-w-6xl mx-auto">
        {title && <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-center mb-6">{title}</h2>}
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left font-medium text-gray-700">Feature</th>
                {products.map((product: any, idx: number) => (
                  <th
                    key={idx}
                    className={`px-4 py-3 text-center font-medium ${
                      product.highlighted ? 'bg-blue-50' : ''
                    }`}
                    style={product.highlighted ? { color: '#2563eb' } : {}}
                  >
                    {product.name || `Product ${idx + 1}`}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {features.map((feature: string, fIdx: number) => (
                <tr key={fIdx} className="border-b border-gray-100">
                  <td className="px-4 py-3 font-medium text-gray-700">{feature}</td>
                  {products.map((product: any, pIdx: number) => (
                    <td
                      key={pIdx}
                      className={`px-4 py-3 text-center ${
                        product.highlighted ? 'bg-blue-50 font-semibold' : 'text-gray-600'
                      }`}
                    >
                      {product.values?.[fIdx] || '-'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

// Cinematic Text Section - Professional
const CinematicTextSection: React.FC<any> = ({ content }) => {
  const { headline, subheadline, alignment = 'center', textEffect = 'gradient' } = content;

  const alignmentClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right'
  };

  const headlineStyle = textEffect === 'gradient'
    ? 'bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent'
    : 'text-gray-900';

  return (
    <section className="p-8 sm:p-12 bg-gray-50">
      <div className="max-w-4xl mx-auto">
        <div className={alignmentClasses[alignment as keyof typeof alignmentClasses] || 'text-center'}>
          {headline && (
            <h2 className={`text-2xl sm:text-3xl md:text-4xl font-semibold mb-4 ${headlineStyle}`}>
              {headline}
            </h2>
          )}
          {subheadline && (
            <p className="text-sm sm:text-base text-gray-600 leading-relaxed max-w-2xl mx-auto">
              {subheadline}
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

// Sticky CTA Section - Professional
const StickyCTASection: React.FC<any> = ({ content }) => {
  const { text, buttonText, price, bgColor = '#ffffff' } = content;

  return (
    <section className="sticky bottom-0 left-0 right-0 py-3 px-4 border-t" style={{ backgroundColor: bgColor }}>
      <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
        <div className="flex-1 min-w-0">
          {text && <p className="text-sm font-medium truncate">{text}</p>}
          {price && <p className="text-lg font-semibold">{price}</p>}
        </div>
        {buttonText && (
          <button className="px-4 py-2 bg-gray-900 text-white rounded-lg font-medium text-sm hover:bg-gray-800 transition-colors whitespace-nowrap">
            {buttonText}
          </button>
        )}
      </div>
    </section>
  );
};

// Frequently Bought Section - Professional
const FrequentlyBoughtSection: React.FC<any> = ({ content }) => {
  const { title = 'Frequently Bought Together', products = [] } = content;

  return (
    <section className="p-6 sm:p-8">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-lg sm:text-xl md:text-2xl font-semibold mb-6">{title}</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {products.map((product: any, idx: number) => (
            <div key={idx} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <img src={getImageUrl(product.image)} alt={product.name} className="w-full h-40 object-cover" />
              <div className="p-3">
                <h3 className="font-medium text-sm mb-1">{product.name}</h3>
                <p className="text-gray-600 text-sm">{product.price}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Hotspots Section - Professional
const HotspotsSection: React.FC<any> = ({ content }) => {
  const { image, hotspots = [] } = content;
  if (!image) return null;

  const fullImage = getImageUrl(image);

  return (
    <section className="p-6 sm:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="relative">
          <img src={fullImage} alt="Product with hotspots" className="w-full rounded-lg" />
          {hotspots.map((hotspot: any, idx: number) => (
            <div
              key={idx}
              className="absolute w-8 h-8 bg-gray-900 rounded-full flex items-center justify-center text-white text-xs font-semibold cursor-pointer hover:bg-gray-700 transition-colors"
              style={{ left: `${hotspot.x}%`, top: `${hotspot.y}%`, transform: 'translate(-50%, -50%)' }}
              title={hotspot.title}
            >
              {idx + 1}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Rich Heading Section - Professional
const RichHeadingSection: React.FC<any> = ({ content }) => {
  const { title, subtitle, alignment = 'center' } = content;

  const alignmentClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right'
  };

  return (
    <section className="p-8 sm:p-12 bg-gray-900 text-white">
      <div className="max-w-4xl mx-auto">
        <div className={alignmentClasses[alignment as keyof typeof alignmentClasses] || 'text-center'}>
          {title && <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold mb-4">{title}</h2>}
          {subtitle && <p className="text-sm sm:text-base md:text-lg opacity-90">{subtitle}</p>}
        </div>
      </div>
    </section>
  );
};

export default ProductDescriptionRenderer;
